import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaCheck, FaCircleXmark, FaCertificate } from "react-icons/fa6";
import api from "../utils/axiosConfig";

type ValidationResult =
  | {
      valid: true;
      code: string;
      courseTitle: string;
      agentName: string;
      companyName: string | null;
      issuedAt: string;
    }
  | { valid: false };

function formatIssuedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
}

const CertificateValidatePage: React.FC = () => {
  const { code: codeParam } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const [inputCode, setInputCode] = useState(codeParam ?? "");
  const [loading, setLoading] = useState(Boolean(codeParam));
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validate = useCallback(async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setResult(null);
      setError("Informe o código de verificação.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const encoded = encodeURIComponent(trimmed);
      const { data } = await api.get<ValidationResult>(
        `public/course-certificates/${encoded}`
      );
      setResult(data);
    } catch {
      setError("Não foi possível validar o certificado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (codeParam) {
      setInputCode(codeParam);
      void validate(codeParam);
    }
  }, [codeParam, validate]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputCode.trim();
    if (!trimmed) {
      setError("Informe o código de verificação.");
      return;
    }
    if (codeParam !== trimmed) {
      navigate(`/certificates/validate/${encodeURIComponent(trimmed)}`);
      return;
    }
    void validate(trimmed);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <FaCertificate className="text-blue-600 text-base" />
          <p className="text-sm font-semibold text-slate-900">
            Preppo · Validação de certificado
          </p>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto w-full px-4 py-6">
        <h1 className="text-xl font-bold text-slate-900 mb-4">
          Validar certificado
        </h1>

        <form onSubmit={onSubmit} className="mb-5">
          <label
            htmlFor="verification-code"
            className="sr-only"
          >
            Código de verificação
          </label>
          <div className="flex gap-2">
            <input
              id="verification-code"
              type="text"
              value={inputCode}
              onChange={(e) => {
                setInputCode(e.target.value.toUpperCase());
                setResult(null);
                setError(null);
              }}
              placeholder="XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              className="flex-1 min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 tracking-wider font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 text-white px-4 py-2 font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors shrink-0"
            >
              {loading ? "..." : "Validar"}
            </button>
          </div>
        </form>

        {error && (
          <p className="text-sm text-red-700 mb-3">{error}</p>
        )}

        {result && !result.valid && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            <FaCircleXmark className="shrink-0" />
            <span>Código inválido. Confira e tente novamente.</span>
          </div>
        )}

        {result?.valid && (
          <div className="rounded-lg border border-green-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 bg-green-50 px-3 py-2 border-b border-green-100 text-sm font-semibold text-green-800">
              <FaCheck className="shrink-0" />
              Certificado válido
            </div>
            <dl className="px-3 py-2.5 text-sm divide-y divide-slate-100">
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-500 shrink-0">Preposto</dt>
                <dd className="font-medium text-slate-900 text-right">
                  {result.agentName}
                </dd>
              </div>
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-500 shrink-0">Curso</dt>
                <dd className="font-medium text-slate-900 text-right">
                  {result.courseTitle}
                </dd>
              </div>
              {result.companyName && (
                <div className="flex justify-between gap-3 py-1.5">
                  <dt className="text-slate-500 shrink-0">Empresa</dt>
                  <dd className="font-medium text-slate-900 text-right">
                    {result.companyName}
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-500 shrink-0">Emitido em</dt>
                <dd className="font-medium text-slate-900 text-right">
                  {formatIssuedAt(result.issuedAt)}
                </dd>
              </div>
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-500 shrink-0">Código</dt>
                <dd className="font-mono font-medium text-slate-900 tracking-wider text-right">
                  {result.code}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          <Link
            to="/"
            className="hover:text-slate-600 underline-offset-2 hover:underline"
          >
            Ir para o login
          </Link>
        </p>
      </main>
    </div>
  );
};

export default CertificateValidatePage;
